import { useEffect, useRef, useState } from "react"
import { useDebounce } from "react-use"

import { Textarea } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { TAG_NOTE_MAX_LENGTH } from "@productlane/zero/mutators/tags"

import { toast } from "@/components/toast"
import { useRole } from "@/lib/useRole"
import { reportServerRejection, useZero } from "@/lib/zero"

const SAVE_DELAY_MS = 700

interface TagNoteProps {
	tagId: string
	note: string | null | undefined
}

export const TagNote = ({ tagId, note }: TagNoteProps) => {
	const zero = useZero()
	const { role } = useRole()
	const saved = note ?? ""
	const [value, setValue] = useState(saved)
	const lastSent = useRef(saved)
	const isFocused = useRef(false)

	// Another teammate's edit replaces the text, but never while this one is typing.
	useEffect(() => {
		if (isFocused.current) return
		lastSent.current = saved
		setValue(saved)
	}, [saved])

	const save = (next: string) => {
		const trimmed = next.trim()
		if (trimmed === lastSent.current) return
		lastSent.current = trimmed
		reportServerRejection(
			zero.mutate(mutators.tags.updateNote({ id: tagId, note: trimmed })),
			(message) => {
				toast.error({ title: "Couldn't save the note", description: message })
			},
		)
	}

	useDebounce(() => save(value), SAVE_DELAY_MS, [value])

	if (role === "VIEWER") {
		if (!saved) return null
		return (
			<p
				data-testid="tag-note"
				className="whitespace-pre-wrap text-[15px] leading-6 text-primary"
			>
				{saved}
			</p>
		)
	}

	return (
		<Textarea
			name="tag-note"
			data-testid="tag-note"
			aria-label="Tag note"
			variant="borderless"
			minRows={1}
			maxLength={TAG_NOTE_MAX_LENGTH}
			placeholder="Add a note for your team"
			value={value}
			onChange={(e) => setValue(e.target.value)}
			onFocus={() => {
				isFocused.current = true
			}}
			onBlur={() => {
				isFocused.current = false
				save(value)
			}}
			inputClassName="!p-0 !text-[15px] leading-6 text-primary"
		/>
	)
}
